import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 * @property {HTMLElement | null} lastClickedItem - The last clicked menu item (for double-click detection on touch devices).
 * @property {number} lastClickTime - Timestamp of the last click.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();
  #touchDeviceQuery = null;

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    // Detect touch devices (tablets) - devices without hover capability
    // On desktop (hover: hover), this will be false, so touch handlers won't be added
    this.#touchDeviceQuery = window.matchMedia('(hover: none)');
    
    // Only add click listeners on touch devices (tablets)
    // On desktop, normal link navigation works (hover opens dropdown, click navigates)
    if (this.#touchDeviceQuery?.matches) {
      this.#setupTouchClickHandlers();
    }

    onDocumentLoaded(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
    lastClickedItem: null,
    lastClickTime: 0,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    // Mark submenu as active for content-visibility optimization
    if (submenu) {
      submenu.dataset.active = '';
    }

    const submenuHeight = submenu ? submenu.offsetHeight : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
      // Remove active state from submenu after animation completes
      if (submenu) {
        delete submenu.dataset.active;
      }
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Setup click handlers for touch devices (tablets).
   * On touch devices, menu items with submenus should:
   * - First click: open dropdown (prevent navigation)
   * - Second click: navigate to link
   * On desktop, normal link navigation works (hover opens dropdown, click navigates)
   */
  #setupTouchClickHandlers() {
    // Only setup on touch devices (tablets) - not on desktop
    if (!this.#touchDeviceQuery?.matches) return;

    // Use event delegation on the header-menu element itself
    // This works even if links are added dynamically
    // Only add listener on touch devices to avoid interfering with desktop navigation
    this.addEventListener('click', this.#handleTouchClickDelegate, {
      signal: this.#abortController.signal,
      capture: true, // Use capture phase to intercept before other handlers
    });
  }

  /**
   * Event delegation handler for touch clicks
   * @param {MouseEvent} event - The click event
   */
  #handleTouchClickDelegate = (event) => {
    // Double-check: only handle on touch devices (tablets)
    // On desktop, let normal link navigation work
    if (!this.#touchDeviceQuery?.matches) {
      // On desktop, allow normal navigation - don't interfere
      return;
    }

    // Check if the click target is a menu link with submenu
    const link = event.target.closest('.menu-list__link[aria-haspopup="true"]');
    if (!(link instanceof HTMLElement)) {
      // Not a menu link with submenu, allow normal navigation
      return;
    }

    // Store original event for preventDefault/stopPropagation
    const originalEvent = event;
    
    // Create a new event-like object with link as currentTarget
    const syntheticEvent = {
      ...event,
      currentTarget: link,
      target: event.target,
      preventDefault: () => originalEvent.preventDefault(),
      stopPropagation: () => originalEvent.stopPropagation(),
    };
    
    this.#handleTouchClick(syntheticEvent);
  };

  /**
   * Handle click on menu links with submenus on touch devices.
   * @param {MouseEvent} event - The click event
   */
  #handleTouchClick = (event) => {
    // Only handle on touch devices
    if (!this.#touchDeviceQuery?.matches) return;

    const link = event.currentTarget;
    if (!(link instanceof HTMLElement)) return;

    // Only handle links with submenus
    if (link.getAttribute('aria-haspopup') !== 'true') return;

    // Check if dropdown is already open for this item
    const isDropdownOpen = link.getAttribute('aria-expanded') === 'true';
    
    const currentTime = Date.now();
    const timeSinceLastClick = currentTime - this.#state.lastClickTime;
    const isSameItem = this.#state.lastClickedItem === link;
    const isDoubleClick = isSameItem && timeSinceLastClick < 500; // 500ms double-click window

    // If dropdown is already open and user clicks again, navigate
    if (isDropdownOpen && isDoubleClick) {
      // Second click on open dropdown: allow navigation
      this.#state.lastClickedItem = null;
      this.#state.lastClickTime = 0;
      // Navigate to the link's href
      if (link instanceof HTMLAnchorElement && link.href) {
        window.location.href = link.href;
      }
      return;
    }

    // If dropdown is already open but not a double-click, close it
    if (isDropdownOpen && !isDoubleClick) {
      // Close dropdown and reset state
      const listItem = link.closest('.menu-list__list-item');
      if (listItem) {
        const deactivateEvent = {
          target: link,
          currentTarget: listItem,
        };
        this.deactivate(deactivateEvent);
      }
      this.#state.lastClickedItem = null;
      this.#state.lastClickTime = 0;
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    // First click: prevent navigation and open dropdown
    event.preventDefault();
    event.stopPropagation();

    // Store click info for double-click detection
    this.#state.lastClickedItem = link;
    this.#state.lastClickTime = currentTime;

    // Find the parent list item
    const listItem = link.closest('.menu-list__list-item');
    if (!listItem) return;

    // Directly activate the menu item by finding it and calling the activate handler
    // The link itself is the menuitem (it has ref="menuitem")
    const menuItem = link; // The link is the menuitem
    
    // Manually trigger activation
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());
    this.removeAttribute('data-animating');

    // Check if this item is already active
    if (menuItem === this.#state.activeItem) return;

    const previouslyActiveItem = this.#state.activeItem;
    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = menuItem;
    this.ariaExpanded = 'true';
    menuItem.ariaExpanded = 'true';

    // Find submenu using the existing helper function
    const submenu = findSubmenu(link);
    if (submenu) {
      submenu.dataset.active = '';
    }

    const submenuHeight = submenu ? submenu.offsetHeight : 0;
    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');

    // Clear the double-click state after a delay
    setTimeout(() => {
      if (this.#state.lastClickedItem === link && Date.now() - this.#state.lastClickTime >= 500) {
        this.#state.lastClickedItem = null;
        this.#state.lastClickTime = 0;
      }
    }, 500);
  };

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
